import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { and, isNil, not, pathEq, path } from 'ramda';

import ApplicantFormService from '../../services/applicantFormService';
import { HOUSEHOLD_MEMBER_TYPES } from './constants';

const { PROSPECT, FINANCIALLY_RESPONSIBLE, NOT_FINANCIALLY_RESPONSIBLE } =
  HOUSEHOLD_MEMBER_TYPES;
export function useApplicantForms({ applicants, propertyId, organizationId }) {
  const applicantFormService = new ApplicantFormService();
  const queryFn = async () => {
    let responses = await applicants.reduce(async (acc, applicant) => {
      const resolvedAcc = await acc;
      const applicantId = applicant.id;
      const memberType = getMemberType(applicant);
      let response = null;
      if (memberType === PROSPECT) {
        response =
          await applicantFormService.getApplicantPrimaryFormInformation(
            applicantId,
            organizationId,
            propertyId,
          );
      } else if (memberType === FINANCIALLY_RESPONSIBLE) {
        response =
          await applicantFormService.getApplicantSecondaryFormInformation(
            applicantId,
            organizationId,
            propertyId,
          );
      } else if (memberType === NOT_FINANCIALLY_RESPONSIBLE) {
        response = await applicantFormService.getApplicantShortFormInformation(
          applicantId,
          organizationId,
          propertyId,
        );
      }
      return { ...resolvedAcc, [applicantId]: response };
    }, {});
    return responses;
  };
  const queryKey = applicants.map((a) => a.id).join('-');
  const enabled = applicants.length > 0 && !!propertyId && !!organizationId;
  const { data, isLoading, refetch } = useQuery(
    `applicationForm-${queryKey}`,
    queryFn,
    {
      enabled,
      refetchOnWindowFocus: false,
    },
  );

  // The application form opens with a full page navigation and closes with
  // history.goBack(), so the browser restores this page frozen from its
  // back/forward cache — nothing remounts, and an application completed
  // seconds ago still reads as incomplete. `persisted` marks that restore.
  useEffect(() => {
    if (!enabled) return undefined;

    const refetchOnRestore = (event) => {
      if (event.persisted) {
        refetch();
      }
    };

    window.addEventListener('pageshow', refetchOnRestore);
    return () => window.removeEventListener('pageshow', refetchOnRestore);
  }, [enabled, refetch]);

  return { data, isLoading };
}

function getMemberType(applicant) {
  const isProspect = () =>
    and(
      not(isNil(applicant)),
      pathEq(['applicantCustomer', 'isProspect'], true)(applicant),
    );

  const isFinanciallyResponsible = () =>
    and(
      and(
        not(isNil(applicant)),
        not(isNil(path(['applicantCustomer'], applicant))),
      ),
      and(
        not(isProspect()),
        pathEq(['applicantType', 'financiallyResponsible'], true)(applicant),
      ),
    );

  const isNotFinanciallyResponsible = () =>
    and(
      and(
        not(isNil(applicant)),
        not(isNil(path(['applicantCustomer'], applicant))),
      ),
      and(not(isProspect()), not(isFinanciallyResponsible())),
    );

  if (isProspect()) {
    return PROSPECT;
  }

  if (isFinanciallyResponsible()) {
    return FINANCIALLY_RESPONSIBLE;
  }

  if (isNotFinanciallyResponsible()) {
    return NOT_FINANCIALLY_RESPONSIBLE;
  }
}
